import { useState, useEffect, useRef } from 'react'
import { User } from '../types/user'
import { formatUserName } from '../utils/name'
import { Avatar } from './Avatar'
import './MentionAutocomplete.css'

interface MentionAutocompleteProps {
  query: string
  users: User[]
  onSelect: (user: User) => void
  onClose: () => void
  position: { top: number; left: number }
}

export function MentionAutocomplete({
  query,
  users,
  onSelect,
  onClose,
  position,
}: MentionAutocompleteProps) {
  const [selectedIndex, setSelectedIndex] = useState(0)
  const containerRef = useRef<HTMLDivElement>(null)

  // Filter users based on query
  const filteredUsers = users.filter((user) => {
    const searchTerm = query.toLowerCase()
    const fullName = `${user.firstName} ${user.lastName}`.toLowerCase()
    const email = user.email?.toLowerCase() || ''
    const username = user.username?.toLowerCase() || ''
    return (
      fullName.includes(searchTerm) ||
      email.includes(searchTerm) ||
      username.includes(searchTerm)
    )
  })

  useEffect(() => {
    setSelectedIndex(0)
  }, [query])

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'ArrowDown') {
        e.preventDefault()
        setSelectedIndex((prev) =>
          prev < filteredUsers.length - 1 ? prev + 1 : prev
        )
      } else if (e.key === 'ArrowUp') {
        e.preventDefault()
        setSelectedIndex((prev) => (prev > 0 ? prev - 1 : 0))
      } else if (e.key === 'Enter') {
        e.preventDefault()
        if (filteredUsers[selectedIndex]) {
          onSelect(filteredUsers[selectedIndex])
        }
      } else if (e.key === 'Escape') {
        e.preventDefault()
        onClose()
      }
    }

    document.addEventListener('keydown', handleKeyDown)
    return () => {
      document.removeEventListener('keydown', handleKeyDown)
    }
  }, [filteredUsers, selectedIndex, onSelect, onClose])

  if (filteredUsers.length === 0) {
    return null
  }

  return (
    <div
      ref={containerRef}
      className="mention-autocomplete"
      style={{ top: `${position.top}px`, left: `${position.left}px` }}
    >
      {filteredUsers.map((user, index) => (
        <div
          key={user.id}
          className={`mention-autocomplete-item ${index === selectedIndex ? 'selected' : ''}`}
          onClick={() => onSelect(user)}
          onMouseEnter={() => setSelectedIndex(index)}
        >
          <Avatar user={user} size="small" />
          <div className="mention-autocomplete-item-info">
            <div className="mention-autocomplete-item-name">
              {formatUserName(user)}
            </div>
            {user.email && (
              <div className="mention-autocomplete-item-email">{user.email}</div>
            )}
          </div>
        </div>
      ))}
    </div>
  )
}

